import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  DollarSign,
  TrendingUp,
  Clock,
  AlertTriangle,
  CheckCircle,
  FileText,
  Users,
  Loader2,
  RefreshCw,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Banknote,
  Target,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Stage colors
const stageColors = {
  new_lead: 'bg-cyan-100 text-cyan-800',
  proposal_sent: 'bg-indigo-100 text-indigo-800',
  proposal_approved: 'bg-purple-100 text-purple-800',
  contract_sent: 'bg-violet-100 text-violet-800',
  contract_signed: 'bg-fuchsia-100 text-fuchsia-800',
  financing_submitted: 'bg-amber-100 text-amber-800',
  financing_approved: 'bg-lime-100 text-lime-800',
  funded: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-red-100 text-red-800',
  cancelled: 'bg-slate-100 text-slate-800',
};

// Stage labels
const stageLabels = {
  new_lead: 'New Lead',
  proposal_sent: 'Proposal Sent',
  proposal_approved: 'Proposal Approved',
  contract_sent: 'Contract Sent',
  contract_signed: 'Contract Signed',
  financing_submitted: 'Financing Submitted',
  financing_approved: 'Financing Approved',
  funded: 'Funded',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

export default function RevenueDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [timeFilter, setTimeFilter] = useState('this_month');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  const fetchDashboard = useCallback(async () => {
    try {
      setRefreshing(true);
      const params = new URLSearchParams({ time_filter: timeFilter });
      if (timeFilter === 'custom' && customDateFrom && customDateTo) {
        params.append('date_from', customDateFrom);
        params.append('date_to', customDateTo);
      }
      const response = await api.get(`/revenue-dashboard?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch revenue dashboard:', error);
      toast.error('Failed to load revenue dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [timeFilter, customDateFrom, customDateTo]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleRefresh = () => {
    fetchDashboard();
  };

  const handleTimeFilterChange = (value) => {
    setTimeFilter(value);
    if (value !== 'custom') {
      setCustomDateFrom('');
      setCustomDateTo('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { metrics, deals_by_stage, revenue_by_stage, funding_sources, stuck_deals, recently_funded, large_deals, thresholds } = data || {};

  // Calculate progress percentage for pipeline
  const totalInPipeline = Object.values(deals_by_stage || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-4" data-testid="revenue-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary">Revenue Dashboard</h1>
          <p className="text-sm text-slate-500">
            Real-time visibility into money flow • {data?.company_scope === 'ptc' ? 'PTC Organization' : 'Company View'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Time Filter */}
          <Select value={timeFilter} onValueChange={handleTimeFilterChange}>
            <SelectTrigger className="w-[140px]" data-testid="time-filter-select">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          
          {timeFilter === 'custom' && (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={customDateFrom}
                onChange={(e) => setCustomDateFrom(e.target.value)}
                className="w-[140px]"
              />
              <span className="text-slate-400">to</span>
              <Input
                type="date"
                value={customDateTo}
                onChange={(e) => setCustomDateTo(e.target.value)}
                className="w-[140px]"
              />
              <Button size="sm" onClick={fetchDashboard}>Apply</Button>
            </div>
          )}
          
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Top Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-slate-50 to-white" data-testid="metric-total-deals">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Deals</p>
                <p className="text-2xl font-bold text-slate-900">{metrics?.total_deals || 0}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-white" data-testid="metric-funded">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Funded Deals</p>
                <p className="text-2xl font-bold text-emerald-700">{metrics?.total_funded || 0}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-white" data-testid="metric-revenue">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-green-600 uppercase tracking-wide">Total Funded Amount</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(metrics?.total_funded_amount || metrics?.total_revenue)}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-white" data-testid="metric-pending">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">Est. Revenue</p>
                <p className="text-2xl font-bold text-amber-700">{formatCurrency(metrics?.total_estimated_revenue)}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-white" data-testid="metric-pending-revenue">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Pipeline Value</p>
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(metrics?.pending_revenue)}</p>
                <p className="text-[10px] text-slate-400 mt-1">Non-funded Deal Amount in scope</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-violet-50 to-white" data-testid="metric-commissions">
          <CardContent className="p-4">
            {/* v3.21-1798 · Revert v1797 mis-placement.  Monthly
                Funded Goal belongs on the Clients page INTERNAL
                Financial Summary Strip (`clients-financial-summary`
                — where the FUNDED AMOUNT / DEAL AMOUNT /
                APPROVED AMOUNT three-card layout the user visually
                identified actually renders).  Revenue Dashboard's
                right-most summary slot restores its Total
                Commissions display. */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-violet-600 uppercase tracking-wide">Total Commissions</p>
                <p className="text-2xl font-bold text-violet-700">{formatCurrency(metrics?.total_commissions)}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {formatCurrency(metrics?.paid_commissions)} paid • {formatCurrency(metrics?.pending_commissions)} pending
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-violet-100 flex items-center justify-center">
                <Banknote className="h-5 w-5 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Pipeline */}
        <div className="lg:col-span-2 space-y-6">
          {/* Deal Pipeline */}
          <Card data-testid="pipeline-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" /> Deal Pipeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {['new_lead', 'proposal_sent', 'proposal_approved', 'contract_sent', 'contract_signed', 'financing_submitted', 'financing_approved', 'funded'].map((stage) => {
                  const count = deals_by_stage?.[stage] || 0;
                  const revenue = revenue_by_stage?.[stage] || 0;
                  const percentage = totalInPipeline > 0 ? (count / totalInPipeline) * 100 : 0;
                  
                  return (
                    <div key={stage} className="flex items-center gap-3">
                      <div className="w-32 shrink-0">
                        <Badge variant="outline" className={`${stageColors[stage]} text-xs`}>
                          {stageLabels[stage]}
                        </Badge>
                      </div>
                      <div className="flex-1">
                        <div className="h-6 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${stage === 'funded' ? 'bg-emerald-500' : 'bg-primary'} transition-all duration-500`}
                            style={{ width: `${Math.max(percentage, count > 0 ? 3 : 0)}%` }}
                          />
                        </div>
                      </div>
                      <div className="w-16 text-right text-sm font-medium text-slate-700">
                        {count}
                      </div>
                      <div className="w-24 text-right text-sm text-slate-500">
                        {formatCurrency(revenue)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Funding Sources */}
          <Card data-testid="funding-sources-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" /> Funding Sources
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-700">{funding_sources?.count?.easyquickfund || 0}</p>
                  <p className="text-xs text-blue-600 font-medium">EasyQuickFund</p>
                  <p className="text-sm text-slate-600 mt-1">{formatCurrency(funding_sources?.revenue?.easyquickfund)}</p>
                </div>
                <div className="text-center p-4 bg-indigo-50 rounded-lg">
                  <p className="text-2xl font-bold text-indigo-700">{funding_sources?.count?.billcom || 0}</p>
                  <p className="text-xs text-indigo-600 font-medium">Bill.com</p>
                  <p className="text-sm text-slate-600 mt-1">{formatCurrency(funding_sources?.revenue?.billcom)}</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-700">{funding_sources?.count?.other || 0}</p>
                  <p className="text-xs text-slate-600 font-medium">Other</p>
                  <p className="text-sm text-slate-600 mt-1">{formatCurrency(funding_sources?.revenue?.other)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stuck Deals */}
          {stuck_deals && stuck_deals.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/30" data-testid="stuck-deals-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="h-4 w-4" /> 
                  Stuck Deals ({data?.stuck_deals_count || stuck_deals.length})
                  <span className="text-xs font-normal text-amber-600 ml-2">No movement in {thresholds?.stuck_days}+ days</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {stuck_deals.slice(0, 5).map((deal) => (
                    <div 
                      key={deal.id} 
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100 cursor-pointer hover:bg-amber-50 transition-colors"
                      onClick={() => navigate(`/clients/${deal.id}`)}
                    >
                      <div>
                        <p className="font-medium text-slate-800">{deal.full_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={`${stageColors[deal.deal_stage]} text-xs`}>
                            {stageLabels[deal.deal_stage] || deal.deal_stage}
                          </Badge>
                          <span className="text-xs text-slate-500">{formatCurrency(deal.deal_amount)}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                          {deal.days_stuck} days
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Recent Activity & Alerts */}
        <div className="space-y-6">
          {/* Recently Funded */}
          <Card className="border-emerald-200 bg-emerald-50/30" data-testid="recently-funded-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center gap-2 text-emerald-800">
                <CheckCircle className="h-4 w-4" /> 
                Recently Funded ({data?.recently_funded_count || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recently_funded && recently_funded.length > 0 ? (
                <div className="space-y-2">
                  {recently_funded.slice(0, 5).map((deal) => (
                    <div 
                      key={deal.id} 
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-100 cursor-pointer hover:bg-emerald-50 transition-colors"
                      onClick={() => navigate(`/clients/${deal.id}`)}
                    >
                      <div>
                        <p className="font-medium text-slate-800">{deal.full_name}</p>
                        <p className="text-xs text-slate-500">{formatDate(deal.funding_date)} • {deal.source}</p>
                      </div>
                      <p className="font-semibold text-emerald-700">{formatCurrency(deal.funded_amount)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">No recently funded deals</p>
              )}
            </CardContent>
          </Card>

          {/* Large Deal Alerts */}
          {large_deals && large_deals.length > 0 && (
            <Card className="border-blue-200 bg-blue-50/30" data-testid="large-deals-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base flex items-center gap-2 text-blue-800">
                  <AlertCircle className="h-4 w-4" /> 
                  Large Deals ({data?.large_deals_count || large_deals.length})
                  <span className="text-xs font-normal text-blue-600 ml-2">{formatCurrency(thresholds?.large_deal_amount)}+</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {large_deals.slice(0, 5).map((deal) => (
                    <div 
                      key={deal.id} 
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100 cursor-pointer hover:bg-blue-50 transition-colors"
                      onClick={() => navigate(`/clients/${deal.id}`)}
                    >
                      <div>
                        <p className="font-medium text-slate-800">{deal.full_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={`${stageColors[deal.deal_stage]} text-xs`}>
                            {stageLabels[deal.deal_stage] || deal.deal_stage}
                          </Badge>
                          {deal.is_funded && (
                            <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 text-xs">
                              Funded
                            </Badge>
                          )}
                        </div>
                      </div>
                      <p className="font-bold text-blue-700">{formatCurrency(deal.deal_amount)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Stats */}
          <Card data-testid="quick-stats-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" /> Quick Stats
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Conversion Rate</span>
                  <span className="font-semibold text-slate-800">
                    {metrics?.total_deals > 0 
                      ? ((metrics?.total_funded / metrics?.total_deals) * 100).toFixed(1)
                      : 0}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Avg Deal Size</span>
                  <span className="font-semibold text-slate-800">
                    {formatCurrency((metrics?.total_funded_amount || metrics?.total_revenue) / (metrics?.total_funded || 1))}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Declined</span>
                  <span className="font-semibold text-red-600">
                    {deals_by_stage?.declined || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Cancelled</span>
                  <span className="font-semibold text-slate-600">
                    {deals_by_stage?.cancelled || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Funded Deals Table */}
      {data?.deals && data.deals.length > 0 && (
        <Card data-testid="funded-deals-table">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-600" /> Funded Deals ({data.deals.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase">Client</th>
                    <th className="text-right py-2 px-3 text-xs font-medium text-slate-500 uppercase">Funded Amount</th>
                    <th className="text-right py-2 px-3 text-xs font-medium text-slate-500 uppercase">Est. Revenue</th>
                    <th className="text-right py-2 px-3 text-xs font-medium text-slate-500 uppercase">Funded Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.deals.map((deal) => (
                    <tr
                      key={deal.id}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/clients/${deal.id}`)}
                      data-testid={`funded-deal-row-${deal.id}`}
                    >
                      <td className="py-2 px-3 font-medium text-slate-800">{deal.client_name}</td>
                      <td className="py-2 px-3 text-right font-semibold text-emerald-700">
                        {deal.funded_amount != null ? formatCurrency(deal.funded_amount) : '-'}
                      </td>
                      <td className="py-2 px-3 text-right text-slate-600">
                        {deal.estimated_revenue != null ? formatCurrency(deal.estimated_revenue) : '-'}
                      </td>
                      <td className="py-2 px-3 text-right text-slate-500">{formatDate(deal.funded_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Version Footer */}
      <div className="text-center text-xs text-slate-400 pt-4 border-t">
        PTC Client Hub {data?.thresholds ? `• Stuck: ${thresholds?.stuck_days} days • Large: ${formatCurrency(thresholds?.large_deal_amount)}+` : ''}
      </div>
    </div>
  );
}
